import type { ReactNode } from 'react';
import type { IntlShape } from 'react-intl';
import type { ChatItem, ToolUseItem } from '../../../../contracts/harness.types';
import type { Artifact } from '../../../../data/projects/artifacts/state';
import { toBlockStatus } from '../../../../data/projects/timeline/genericToolUseStatus';
import {
  DEFAULT_OBJECT_CARD_VARIANT,
  objectActionLabel,
} from '../../../../data/projects/timeline/object-card/objectToolMapping';
import type { WrittenObject } from '../../../../data/projects/timeline/object-card/objectWrites';
import {
  objectKindForTool,
  writtenObjects,
} from '../../../../data/projects/timeline/object-card/objectWrites';
import { resolveCreatedTitle } from '../../../../data/projects/timeline/object-card/resolveCreatedTitle';
import { ObjectCreationCard } from './ObjectCreationCard';

export interface ObjectCardDeps {
  /** Mount the artifact panel beside the chat. */
  onOpenArtifact: (artifact: Artifact) => void;
  /** Localizes card labels and resolved titles. */
  intl: IntlShape;
  /**
   * Full timeline — used to resolve a created object's title from a later
   * tool call (e.g. a session named by `set_session_title`, or a document
   * named by `rename_session_document`, after creation).
   */
  items?: ChatItem[];
  /** The app's artifact deriver, for the card's Open action and its title. */
  resolveArtifact: (item: ToolUseItem) => Artifact | undefined;
}

/**
 * Render a single created object (session / patient / document) as a card.
 *
 * Handles the running ("generating") and completed states only — an errored
 * or cancelled create falls back to the generic tool row (which owns the
 * destructive styling and the recoverable-error suppression). Returns `null`
 * for any tool that isn't a mapped object create.
 */
export const renderObjectCreationToolUse = (
  item: ToolUseItem,
  deps: ObjectCardDeps
): ReactNode | null => {
  const kind = objectKindForTool(item.name);
  if (!kind) return null;
  // HITL / streaming drafts are not in-flight creates. `toBlockStatus` maps
  // both onto `running`, which would paint the generating spinner forever.
  if (item.status === 'pending_approval' || item.status === 'streaming_input') {
    return null;
  }

  const status = toBlockStatus(item.status);
  const title = resolveCreatedTitle(item, kind, deps);
  const actionLabel = objectActionLabel(kind, deps.intl);

  if (status === 'running') {
    return (
      <ObjectCreationCard
        kind={kind}
        status="generating"
        title={title}
        actionLabel={actionLabel}
        variant={DEFAULT_OBJECT_CARD_VARIANT}
      />
    );
  }

  if (status === 'success') {
    const artifact = deps.resolveArtifact(item);
    return (
      <ObjectCreationCard
        kind={kind}
        status="completed"
        title={title}
        actionLabel={actionLabel}
        variant={DEFAULT_OBJECT_CARD_VARIANT}
        onAction={artifact ? () => deps.onOpenArtifact(artifact) : undefined}
      />
    );
  }

  return null;
};

/**
 * A run of two or more written objects renders as one list. The list itself is
 * the app's chat object list, which joins patient and reference lookups, so it
 * crosses as `renderObjectList`.
 */
export const renderObjectCreationBulk = (
  members: ToolUseItem[],
  renderObjectList: (objects: WrittenObject[]) => ReactNode
): ReactNode | null => {
  const objects = writtenObjects(members);
  return objects.length >= 2 ? renderObjectList(objects) : null;
};
